import { useState } from 'react';
import { Group, Radio, Stack, Text } from '@mantine/core';
import { useMakeDefaultTemplate } from 'src/data-capture/hooks/templates/useMakeDefaultTemplate';
import type { TemplateWithoutSections } from 'src/data-capture/hooks/templates/useTemplates';
import { Button } from 'src/design-system/Button';
import { Modal } from 'src/design-system/Modal';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { DefaultTemplateBadge } from './DefaultTemplateBadge';
import { WrittenNotesWontChange } from './MakeDefaultTemplateModal';

type Props = {
  opened: boolean;
  onClose: () => void;
  templates: TemplateWithoutSections[];
};

export function ChooseDefaultTemplateModal({ opened, onClose, templates }: Props) {
  const { makeDefault, isMakingDefault } = useMakeDefaultTemplate();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const activeTemplates = templates.filter(template => template.isActive);
  const selected = activeTemplates.find(template => template.id === selectedId);

  const close = () => {
    setSelectedId(null);
    onClose();
  };

  const handleConfirm = async () => {
    if (!selected) return;
    if (await makeDefault(selected)) {
      close();
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={close}
      title="Choose your default meeting note template"
      size={480}
    >
      <Text size="md" fw={FONT_WEIGHTS.medium} c="gray.9" mt="xs">
        We use it to write notes automatically after every recorded meeting, for everyone at your
        firm.
      </Text>
      <Radio.Group value={selectedId} onChange={setSelectedId} mt="md">
        <Stack gap="xs">
          {activeTemplates.map(template => (
            <Radio.Card
              key={template.id}
              value={template.id}
              px="sm"
              py="xs"
              style={{
                borderRadius: 12,
                borderColor:
                  template.id === selectedId
                    ? 'var(--mantine-color-brand-6)'
                    : 'var(--default-border-color)',
              }}
            >
              <Group gap="sm" wrap="nowrap">
                <Radio.Indicator color="brand" />
                <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.9" style={{ minWidth: 0 }}>
                  {template.displayName}
                </Text>
                {template.isDefault && <DefaultTemplateBadge label="Current default" />}
              </Group>
            </Radio.Card>
          ))}
        </Stack>
      </Radio.Group>
      <WrittenNotesWontChange />
      <Group justify="flex-end" gap="xs" mt="lg">
        <Button variant="secondary" size="md" onClick={close} disabled={isMakingDefault}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="md"
          onClick={handleConfirm}
          loading={isMakingDefault}
          disabled={!selected}
        >
          {selected ? `Make ${selected.displayName} the default` : 'Make default'}
        </Button>
      </Group>
    </Modal>
  );
}
